import { Box, Divider, FillPreview, FormField } from '@wix/design-system';
import React, { useContext } from 'react';

import { RicosContext } from './../../../ricos-context';
import type {
  PollData_Design_PollDesign_Background_Gradient,
  PollData_Design_PollDesign_Background_Type as PollBackgroundType,
} from './../../../ricos-schema/next-schema';
import { PollData_Design_PollDesign_Background_Type } from './../../../ricos-schema/next-schema';
import type { LogBiFn } from './../../../_shared/bi-context-provider/bi-context-provider';
import { logInvariant } from './../../../_shared/log-invariant';
import { useNodeAttrs } from './../../../_shared/use-node-attrs';
import type { ThumbnailPickerProps } from './../../../ui-components';
import { SliderWithInput, ThumbnailPicker } from './../../../ui-components';

import { ColorIcon } from '../../assets/icons/ColorIcon';
import { GradientIcon } from '../../assets/icons/GradientIcon';
import { ImageIcon } from '../../assets/icons/ImageIcon';
import {
  DESIGN_SETTINGS_ANSWERS_CORNER_RADIUS_INPUT,
  DESIGN_SETTINGS_ANSWERS_CORNER_RADIUS_SLIDER,
  DESIGN_SETTINGS_BACKGROUND_COLOR_BUTTON,
  DESIGN_SETTINGS_BACKGROUND_GRADIENT_BUTTON,
  DESIGN_SETTINGS_BACKGROUND_IMAGE_BUTTON,
  DESIGN_SETTINGS_BACKGROUND_THUMBNAIL,
  DESIGN_SETTINGS_BACKGROUND_THUMBNAIL_PICKER,
  DESIGN_SETTINGS_CORNER_RADIUS_INPUT,
  DESIGN_SETTINGS_CORNER_RADIUS_SLIDER,
} from '../../data-hooks';
import { BACKGROUND_PRESETS } from '../../defaults';
import { Section } from './section';

type DesignSettingsSectionProps = {
  nodeId: string;
  logBi: LogBiFn;
};

export const DesignSettingsSection: React.FC<DesignSettingsSectionProps> = ({ nodeId, logBi }) => {
  const { t } = useContext(RicosContext);
  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, 'POLL');

  if (!pmNodeAttrs) {
    logInvariant(Error('Poll settings: node attrs not found'));
    return null;
  }

  const backgroundType =
    pmNodeAttrs.design?.poll?.background?.type || PollData_Design_PollDesign_Background_Type.COLOR;

  const handlePollBorderRadiusChange = (borderRadius: number) => {
    patchPmNodeAttrs({
      design: {
        ...pmNodeAttrs.design,
        poll: { ...pmNodeAttrs.design?.poll, borderRadius },
      },
    });
  };

  const handleOptionBorderRadiusChange = (borderRadius: number) => {
    patchPmNodeAttrs({
      design: {
        ...pmNodeAttrs.design,
        options: { ...pmNodeAttrs.design?.options, borderRadius },
      },
    });
  };

  const handleBackgroundChange: ThumbnailPickerProps['onChange'] = value => {
    const backgroundType = value as PollData_Design_PollDesign_Background_Type;
    const backgroundPreset = BACKGROUND_PRESETS[backgroundType][0];
    const bgValue =
      backgroundType === PollData_Design_PollDesign_Background_Type.GRADIENT
        ? { gradient: JSON.parse(backgroundPreset) }
        : backgroundType === PollData_Design_PollDesign_Background_Type.IMAGE
        ? { image: { src: { url: backgroundPreset } } }
        : { color: backgroundPreset };

    patchPmNodeAttrs({
      design: {
        ...pmNodeAttrs.design,
        poll: {
          ...pmNodeAttrs.design?.poll,
          background: { ...bgValue, type: backgroundType as PollBackgroundType },
        },
      },
    });
  };

  const handleBackgroundColorChange = (value: $TSFixMe) => {
    const bgValue =
      backgroundType === PollData_Design_PollDesign_Background_Type.GRADIENT
        ? { gradient: JSON.parse(value) }
        : backgroundType === PollData_Design_PollDesign_Background_Type.IMAGE
        ? { image: { src: { url: value } } }
        : { color: value };

    patchPmNodeAttrs({
      design: {
        ...pmNodeAttrs.design,
        poll: {
          ...pmNodeAttrs.design?.poll,
          background: { ...bgValue, type: backgroundType as PollBackgroundType },
        },
      },
    });
  };

  const dataSource: Omit<ThumbnailPickerProps['dataSource'][0], 'dataHook'>[] = [
    {
      value: PollData_Design_PollDesign_Background_Type.COLOR,
      label: t('Poll_PollSettings_Tab_Design_Section_Background_Color'),
      renderBackgroundImage: props => <ColorIcon {...props} />,
    },
    {
      value: PollData_Design_PollDesign_Background_Type.GRADIENT,
      label: t('Poll_PollSettings_Tab_Design_Section_Background_Gradient'),
      renderBackgroundImage: props => <GradientIcon {...props} />,
    },
    {
      value: PollData_Design_PollDesign_Background_Type.IMAGE,
      label: t('Poll_PollSettings_Tab_Design_Section_Background_Pattern'),
      renderBackgroundImage: props => <ImageIcon {...props} />,
    },
  ];

  return (
    <>
      <Section title={t('Poll_PollSettings_Tab_Design_Section_Background_Header')}>
        <ThumbnailPicker
          dataHook={DESIGN_SETTINGS_BACKGROUND_THUMBNAIL_PICKER}
          logBi={logBi}
          dataSource={dataSource.map(args => ({
            ...args,
            dataHook: DESIGN_SETTINGS_BACKGROUND_THUMBNAIL + args.value,
          }))}
          selectedValue={backgroundType}
          title={t('Poll_PollSettings_Tab_Design_Section_Background_Choose')}
          onChange={handleBackgroundChange}
        />
      </Section>
      <FormField label={t(PICK_BACKGROUND_LABELS[backgroundType])}>
        <Box gap="SP2" flexWrap="wrap" display="flex">
          {BACKGROUND_PRESETS[backgroundType].map((color, index) => {
            const background = pmNodeAttrs.design?.poll?.background;
            return (
              <Box width="38px" key={index}>
                <FillPreview
                  dataHook={PICK_BACKGROUND_DATA_HOOKS[backgroundType]}
                  selected={
                    !background
                      ? false
                      : background.type === 'COLOR'
                      ? color === background?.color
                      : background.type === 'IMAGE'
                      ? color === background?.image?.src?.url
                      : background.type === 'GRADIENT'
                      ? color === JSON.stringify(background.gradient)
                      : false
                  }
                  onClick={() => {
                    logBi({
                      dataHook: PICK_BACKGROUND_DATA_HOOKS[backgroundType],
                      value:
                        pmNodeAttrs.design?.poll?.background?.type === 'GRADIENT'
                          ? gradientColorObjToString(JSON.parse(color))
                          : color,
                    });
                    handleBackgroundColorChange(color);
                  }}
                  fill={
                    pmNodeAttrs.design?.poll?.background?.type === 'GRADIENT'
                      ? gradientColorObjToString(JSON.parse(color))
                      : color
                  }
                />
              </Box>
            );
          })}
        </Box>
      </FormField>
      <Box marginInline="-18px">
        <Divider />
      </Box>
      <Section title={t('Poll_PollSettings_Tab_Design_Section_CornerRadius_Header')}>
        <SliderWithInput
          min={0}
          max={20}
          label={t('Poll_PollSettings_Tab_Design_Section_CornerRadius_Poll')}
          onChange={handlePollBorderRadiusChange}
          value={pmNodeAttrs.design?.poll?.borderRadius ?? 0}
          sliderDataHook={DESIGN_SETTINGS_CORNER_RADIUS_SLIDER}
          inputDataHook={DESIGN_SETTINGS_CORNER_RADIUS_INPUT}
          logBi={logBi}
        />
        <SliderWithInput
          min={0}
          max={10}
          label={t('Poll_PollSettings_Tab_Design_Section_CornerRadius_Answers')}
          onChange={handleOptionBorderRadiusChange}
          value={pmNodeAttrs.design?.options?.borderRadius ?? 0}
          sliderDataHook={DESIGN_SETTINGS_ANSWERS_CORNER_RADIUS_SLIDER}
          inputDataHook={DESIGN_SETTINGS_ANSWERS_CORNER_RADIUS_INPUT}
          logBi={logBi}
        />
      </Section>
    </>
  );
};

const PICK_BACKGROUND_LABELS: Record<PollData_Design_PollDesign_Background_Type, string> = {
  [PollData_Design_PollDesign_Background_Type.COLOR]:
    'Poll_PollSettings_Tab_Design_Section_Background_Color_Pick',
  [PollData_Design_PollDesign_Background_Type.IMAGE]:
    'Poll_PollSettings_Tab_Design_Section_Background_Pattern_Pick',
  [PollData_Design_PollDesign_Background_Type.GRADIENT]:
    'Poll_PollSettings_Tab_Design_Section_Background_Gradient_Pick',
};

const PICK_BACKGROUND_DATA_HOOKS: Record<PollData_Design_PollDesign_Background_Type, string> = {
  [PollData_Design_PollDesign_Background_Type.COLOR]: DESIGN_SETTINGS_BACKGROUND_COLOR_BUTTON,
  [PollData_Design_PollDesign_Background_Type.IMAGE]: DESIGN_SETTINGS_BACKGROUND_IMAGE_BUTTON,
  [PollData_Design_PollDesign_Background_Type.GRADIENT]: DESIGN_SETTINGS_BACKGROUND_GRADIENT_BUTTON,
};

const gradientColorObjToString = (gradient: PollData_Design_PollDesign_Background_Gradient) => {
  return `linear-gradient(${gradient?.angle}deg, ${gradient.startColor} 0%, ${gradient.lastColor} 100%)`;
};
